import { jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useMemo } from 'react';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { isPortaledEvent, accept } from '../utils/event.js';
import { createFocusManager, findClosestFocusable, isFocusWithin } from '../utils/focus.js';
import { LastInteraction } from '../utils/interaction.js';
import { mergeProps } from '../utils/merge-props.js';
import { isAnyModifierPressed } from '../utils/modifiers.js';
import { take } from '../utils/ref.js';
import { isRadioInput, arrowsHandledNativelyByElement } from './toolbar-primitive.utils.js';

const ARROW_DIRECTIONS = {
    horizontal: ['ArrowRight', 'ArrowLeft'],
    vertical: ['ArrowDown', 'ArrowUp'],
};
/**
 * A toolbar is a container for grouping a set of controls,
 * such as buttons, menubuttons, or checkboxes.
 *
 * When a set of controls is visually presented as a group, the toolbar role can be used
 * to communicate the presence and purpose of the grouping to screen reader users. Grouping
 * controls into toolbars can also be an effective way of reducing the number of tab stops
 * in the keyboard interface.
 *
 * @see https://fpl.figma.design/primitives/toolbar/
 */
const ToolbarPrimitive = forwardRef(({ orientation = 'horizontal', ...props }, refIn) => {
    const ref = useSharedRef(refIn);
    const lastElement = useRef(null);
    // we know ref is stable here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const focusManager = useMemo(() => createFocusManager(ref, { criteria: 'focusable' }), []);
    const ourProps = {
        onFocus(e) {
            if (isPortaledEvent(e))
                return;
            const root = ref.current;
            if (!root)
                return;
            // It is possible something else has already decided to move focus elsewhere when we
            // receive the focus event. For example, it happens with our useAppFocus hook in a capturing
            // focus listener. We only want to run this logic if focus is still within the toolbar.
            if (!isFocusWithin(root))
                return;
            const entering = !root.contains(e.relatedTarget);
            if (entering && LastInteraction.type === 'pointer') {
                // If entering via the mouse, we don't want to jump focus to where it was last,
                // and instead let the browser handle focus as usual. We can also clear the lastElement.
                lastElement.current = null;
                return;
            }
            const lastElementInside = root.contains(lastElement.current);
            if (entering && lastElementInside) {
                // Take lastElement.current before focusing to avoid an infinite loop, since the
                // `onFocus` callback would be called synchronously.
                take(lastElement).focus();
            }
            else if (!lastElementInside) {
                // If the last element was removed from the DOM, clear it so it can be garbage collected.
                lastElement.current = null;
            }
        },
        onBlur(e) {
            // We handle lastElement tracking for the tab key in onKeyDownCapture, so skip here
            if (LastInteraction.key === 'Tab' || isPortaledEvent(e))
                return;
            const root = ref.current;
            if (!root)
                return;
            const leaving = !root.contains(e.relatedTarget);
            if (leaving)
                lastElement.current = e.target;
        },
        onPointerDownCapture(e) {
            if (isPortaledEvent(e))
                return;
            const focusable = findClosestFocusable(e.target, ref.current);
            if (focusable) {
                // If a menu button is clicked, the toolbar won't actually receive focus by the time
                // the menu opens and steals focus away. In this case we want to track that the clicked
                // element was the last element, so that if we return to the toolbar via the keyboard
                // later, we refocus the correct element.
                lastElement.current = focusable;
            }
        },
        onKeyDownCapture(e) {
            if (isPortaledEvent(e))
                return;
            switch (e.code) {
                case 'Tab': {
                    // Store the currently focused element so we can return to it when re-entering the toolbar
                    lastElement.current = e.target;
                    // We move the focus manually to the first or last tabbable element, and then let the tab
                    // event natively happen to move focus out of the toolbar.
                    if (e.shiftKey) {
                        focusManager.focusFirst();
                    }
                    else {
                        focusManager.focusLast();
                    }
                    // Don't accept the event
                    return;
                }
                case 'ArrowRight':
                case 'ArrowDown': {
                    if (ARROW_DIRECTIONS[orientation].includes(e.code) && !isAnyModifierPressed(e)) {
                        // Stop at the end if the key is held down
                        focusManager.focusNext({ wrap: !e.repeat });
                        break;
                    }
                    return;
                }
                case 'ArrowLeft':
                case 'ArrowUp': {
                    if (ARROW_DIRECTIONS[orientation].includes(e.code) && !isAnyModifierPressed(e)) {
                        // Stop at the end if the key is held down
                        focusManager.focusPrevious({ wrap: !e.repeat });
                        break;
                    }
                    return;
                }
                default:
                    // Skip accepting the event by default
                    return;
            }
            accept(e);
        },
        onKeyDown(e) {
            if (isPortaledEvent(e) || isAnyModifierPressed(e))
                return;
            switch (e.code) {
                case 'ArrowRight':
                case 'ArrowDown': {
                    if (
                    // Already handled in the capturing phase
                    ARROW_DIRECTIONS[orientation].includes(e.code) ||
                        arrowsHandledNativelyByElement(e.target)) {
                        return;
                    }
                    // Stop at the end if the key is held down
                    focusManager.focusNext({ wrap: !e.repeat });
                    break;
                }
                case 'ArrowLeft':
                case 'ArrowUp': {
                    if (
                    // Already handled in the capturing phase
                    ARROW_DIRECTIONS[orientation].includes(e.code) ||
                        arrowsHandledNativelyByElement(e.target)) {
                        return;
                    }
                    // Stop at the end if the key is held down
                    focusManager.focusPrevious({ wrap: !e.repeat });
                    break;
                }
                case 'Home': {
                    focusManager.focusFirst();
                    break;
                }
                case 'End': {
                    focusManager.focusLast();
                    break;
                }
                case 'Enter': {
                    const target = e.target;
                    if (isRadioInput(target)) {
                        target.click();
                        break;
                    }
                    return;
                }
                default:
                    // Skip accepting the event by default
                    return;
            }
            accept(e);
        },
    };
    return (jsx("div", { "data-fpl-component": "primitive", ...mergeProps(props, ourProps), ref: ref, role: "toolbar", "aria-orientation": orientation }));
});
ToolbarPrimitive.displayName = 'ToolbarPrimitive';

export { ToolbarPrimitive };
//# sourceMappingURL=toolbar-primitive.js.map
